/**
 * Fetch published SubscriptionPlan rows from the backend for the pricing
 * page. The endpoint is public (no auth required) and supports `?language=`
 * for translated name/benefits.
 *
 * Mirrors the portal's `getSubscriptionPlans` helper in shape, but uses
 * raw `fetch` since the company site doesn't carry an axios client. We
 * never throw — if the network fails we return an empty list and the
 * caller falls back to the static i18n cards so the marketing page
 * always renders something even when the API is unreachable.
 */

function getApiBase() {
  const env = process.env.REACT_APP_API_URL || '';
  if (env.trim()) {
    const base = env.replace(/\/+$/, '');
    return base.endsWith('/v1') ? base : base + '/v1';
  }
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:3000/v1';
    return window.location.origin + '/v1';
  }
  return 'http://localhost:3000/v1';
}

export async function fetchSubscriptionPlans(language = 'en') {
  try {
    const url = `${getApiBase()}/subscription-plans?language=${encodeURIComponent(language)}`;
    const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : (data?.data ?? data?.items ?? []);
    return list.filter((p) => p && (p.isActive !== false));
  } catch (_) {
    return [];
  }
}

export function getCurrencySymbol(currency) {
  const c = String(currency || 'USD').toUpperCase();
  if (c === 'ILS') return '₪';
  if (c === 'USD') return '$';
  if (c === 'EUR') return '€';
  if (c === 'GBP') return '£';
  return c;
}

/** Format the numeric monthlyPrice with currency + per-month suffix.
 *  monthlyPrice = 0 → returns null (caller renders the "Free" label). */
export function formatMonthlyPrice(plan) {
  const monthly = Number(plan?.monthlyPrice ?? 0);
  if (!Number.isFinite(monthly) || monthly <= 0) return null;
  const symbol = getCurrencySymbol(plan?.currency);
  const amount = Number.isInteger(monthly) ? String(monthly) : monthly.toFixed(2);
  return { symbol, amount };
}

/**
 * Resolve the per-month price that should be displayed for the chosen
 * billing period.
 *
 * `plan.yearlyPrice` is the per-month-equivalent rate when the visitor
 * commits to a yearly subscription (e.g. monthly $49, yearly $39/mo
 * billed once a year for $468). Mirrors the salon-portal Membership page
 * shape so the prices the visitor sees here match what they'll see on
 * /membership/checkout/<id> after onboarding.
 *
 * Returns:
 *   - { isFree: true } when the plan is free
 *   - { symbol, amount, monthlyAmount, yearlyTotalAmount, percentSaved }
 *     where `amount` is the per-month rate to render and `monthlyAmount`
 *     is the regular monthly price for the strike-through comparison.
 */
export function resolveDisplayPrice(plan, period = 'MONTHLY') {
  const monthly = Number(plan?.monthlyPrice ?? 0);
  if (!Number.isFinite(monthly) || monthly <= 0) return { isFree: true };

  const symbol = getCurrencySymbol(plan?.currency);
  const yearlyMonthly = plan?.yearlyPrice != null && plan.yearlyPrice !== ''
    ? Number(plan.yearlyPrice)
    : monthly;

  const displayPerMonth = period === 'YEARLY' ? yearlyMonthly : monthly;
  const yearlyTotal = yearlyMonthly * 12;
  const percentSaved = monthly > 0 && yearlyMonthly < monthly
    ? Math.round(((monthly - yearlyMonthly) / monthly) * 100)
    : 0;

  const fmt = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(2));
  return {
    isFree: false,
    symbol,
    amount: fmt(displayPerMonth),
    monthlyAmount: fmt(monthly),
    yearlyTotalAmount: fmt(yearlyTotal),
    percentSaved,
  };
}

/** Pull the localized benefits array. Backend already swaps to the
 *  requested language when `?language=` is passed, but we accept the
 *  per-language fallback fields too for older API shapes. */
export function getPlanBenefits(plan) {
  if (!plan) return [];
  if (Array.isArray(plan.benefits)) return plan.benefits;
  return plan.benefitsEn || plan.benefitsHe || plan.benefitsAr || [];
}
